import { useState, useCallback } from 'react';
import { Palette, Copy, Check, RefreshCw, Upload, Droplet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

interface Color {
    hex: string;
    rgb: string;
    hsl: string;
}

const ColorPicker = () => {
    const [selectedColor, setSelectedColor] = useState('#6366f1');
    const [palette, setPalette] = useState<string[]>(['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316']);
    const [extractedColors, setExtractedColors] = useState<string[]>([]);
    const [copied, setCopied] = useState<string | null>(null);

    const hexToRgb = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return 'rgb(0, 0, 0)';
        return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
    };

    const hexToHsl = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return 'hsl(0, 0%, 0%)';

        let r = parseInt(result[1], 16) / 255;
        let g = parseInt(result[2], 16) / 255;
        let b = parseInt(result[3], 16) / 255;

        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s, l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        } else {
            s = 0;
        }

        return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
    };

    const getColorInfo = (hex: string): Color => ({
        hex: hex.toUpperCase(),
        rgb: hexToRgb(hex),
        hsl: hexToHsl(hex),
    });

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(text);
        toast.success('Copied!');
        setTimeout(() => setCopied(null), 2000);
    };

    const generateRandomPalette = () => {
        const colors = Array.from({ length: 5 }, () => {
            const letters = '0123456789ABCDEF';
            let color = '#';
            for (let i = 0; i < 6; i++) {
                color += letters[Math.floor(Math.random() * 16)];
            }
            return color;
        });
        setPalette(colors);
        toast.success('New palette generated!');
    };

    const generateHarmoniousPalette = (type: 'complementary' | 'analogous' | 'triadic') => {
        const hex = selectedColor;
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return;

        let r = parseInt(result[1], 16) / 255;
        let g = parseInt(result[2], 16) / 255;
        let b = parseInt(result[3], 16) / 255;

        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s, l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        } else {
            s = 0;
        }

        const hslToHex = (h: number, s: number, l: number): string => {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
            const g = Math.round(hue2rgb(p, q, h) * 255);
            const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
        };

        let newPalette: string[] = [];

        switch (type) {
            case 'complementary':
                newPalette = [
                    hslToHex(h, s, l),
                    hslToHex(h, s, l * 0.7),
                    hslToHex((h + 0.5) % 1, s, l),
                    hslToHex((h + 0.5) % 1, s, l * 0.7),
                    hslToHex(h, s * 0.5, l),
                ];
                break;
            case 'analogous':
                newPalette = [
                    hslToHex((h - 0.1 + 1) % 1, s, l),
                    hslToHex((h - 0.05 + 1) % 1, s, l),
                    hslToHex(h, s, l),
                    hslToHex((h + 0.05) % 1, s, l),
                    hslToHex((h + 0.1) % 1, s, l),
                ];
                break;
            case 'triadic':
                newPalette = [
                    hslToHex(h, s, l),
                    hslToHex((h + 0.333) % 1, s, l),
                    hslToHex((h + 0.666) % 1, s, l),
                    hslToHex(h, s, l * 0.7),
                    hslToHex((h + 0.333) % 1, s, l * 0.7),
                ];
                break;
        }

        setPalette(newPalette);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} palette generated!`);
    };

    const extractColorsFromImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = 100;
            canvas.height = 100;
            ctx?.drawImage(img, 0, 0, 100, 100);

            const imageData = ctx?.getImageData(0, 0, 100, 100).data;
            if (!imageData) return;

            const colorCounts: { [key: string]: number } = {};

            for (let i = 0; i < imageData.length; i += 40) {
                const r = imageData[i];
                const g = imageData[i + 1];
                const b = imageData[i + 2];
                const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
                colorCounts[hex] = (colorCounts[hex] || 0) + 1;
            }

            const sortedColors = Object.entries(colorCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([color]) => color);

            setExtractedColors(sortedColors);
            toast.success('Colors extracted!');
        };

        img.src = URL.createObjectURL(file);
    }, []);

    const colorInfo = getColorInfo(selectedColor);

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <Palette className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Color Picker & Palette</h1>
                        <p className="text-muted-foreground">Pick colors and generate beautiful palettes</p>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="picker">
                <TabsList className="mb-6">
                    <TabsTrigger value="picker">Color Picker</TabsTrigger>
                    <TabsTrigger value="palette">Palette Generator</TabsTrigger>
                    <TabsTrigger value="extract">Extract from Image</TabsTrigger>
                </TabsList>

                {/* Color Picker */}
                <TabsContent value="picker">
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Pick a Color</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-4 items-center mb-6">
                                    <input
                                        type="color"
                                        value={selectedColor}
                                        onChange={(e) => setSelectedColor(e.target.value)}
                                        className="w-20 h-20 rounded-lg cursor-pointer border-0"
                                    />
                                    <Input
                                        value={selectedColor.toUpperCase()}
                                        onChange={(e) => setSelectedColor(e.target.value)}
                                        className="font-mono text-lg"
                                    />
                                </div>

                                <div
                                    className="w-full h-32 rounded-xl mb-6 shadow-lg"
                                    style={{ backgroundColor: selectedColor }}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Color Values</CardTitle>
                                <CardDescription>Click to copy</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {Object.entries(colorInfo).map(([format, value]) => (
                                    <div
                                        key={format}
                                        onClick={() => copyToClipboard(value)}
                                        className="flex justify-between items-center p-4 rounded-lg bg-muted hover:bg-muted/80 cursor-pointer transition-colors"
                                    >
                                        <span className="font-medium uppercase">{format}</span>
                                        <div className="flex items-center gap-2">
                                            <code className="font-mono">{value}</code>
                                            {copied === value ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Palette Generator */}
                <TabsContent value="palette">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-start">
                                <div>
                                    <CardTitle>Generate Palette</CardTitle>
                                    <CardDescription>Create harmonious color palettes</CardDescription>
                                </div>
                                <Button onClick={generateRandomPalette} variant="outline">
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Random
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2 mb-6">
                                <Button variant="outline" onClick={() => generateHarmoniousPalette('complementary')}>
                                    Complementary
                                </Button>
                                <Button variant="outline" onClick={() => generateHarmoniousPalette('analogous')}>
                                    Analogous
                                </Button>
                                <Button variant="outline" onClick={() => generateHarmoniousPalette('triadic')}>
                                    Triadic
                                </Button>
                            </div>

                            <div className="flex rounded-xl overflow-hidden shadow-lg">
                                {palette.map((color, i) => (
                                    <div
                                        key={i}
                                        onClick={() => copyToClipboard(color)}
                                        className="flex-1 h-32 cursor-pointer hover:scale-105 transition-transform flex items-end justify-center pb-2"
                                        style={{ backgroundColor: color }}
                                    >
                                        <span className="text-xs font-mono px-2 py-1 rounded bg-black/30 text-white">
                                            {color}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Extract from Image */}
                <TabsContent value="extract">
                    <Card>
                        <CardHeader>
                            <CardTitle>Extract Colors from Image</CardTitle>
                            <CardDescription>Upload an image to extract its dominant colors</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <label className="block">
                                <div className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                                    <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                    <p className="font-medium">Drop image here or click to upload</p>
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={extractColorsFromImage}
                                    className="hidden"
                                />
                            </label>

                            {extractedColors.length > 0 && (
                                <div className="mt-6">
                                    <p className="text-sm font-medium mb-3">Extracted Colors</p>
                                    <div className="flex flex-wrap gap-3">
                                        {extractedColors.map((color, i) => (
                                            <div
                                                key={i}
                                                onClick={() => copyToClipboard(color)}
                                                className="w-16 h-16 rounded-lg cursor-pointer hover:scale-110 transition-transform shadow-lg flex items-end justify-center pb-1"
                                                style={{ backgroundColor: color }}
                                            >
                                                <span className="text-[8px] font-mono bg-black/30 text-white px-1 rounded">
                                                    {color}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ColorPicker;
